import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  MessageSquare,
  DollarSign,
  Clock,
  Star
} from 'lucide-react';
import type { TopQuestion } from '@/types/ai-admin';

interface TopQuestionsTableProps {
  questions: TopQuestion[];
}

type SortField = 'question' | 'count' | 'avgRating' | 'avgResponseTime' | 'avgCost';
type SortOrder = 'asc' | 'desc';

export default function TopQuestionsTable({ questions }: TopQuestionsTableProps) {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('count');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Sort questions based on current sort field and order
  const sortedQuestions = [...questions].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];

    if (typeof aVal === 'string') {
      return sortOrder === 'asc'
        ? aVal.localeCompare(bVal as string)
        : (bVal as string).localeCompare(aVal);
    }

    return sortOrder === 'asc'
      ? (aVal as number) - (bVal as number)
      : (bVal as number) - (aVal as number);
  });

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Get agent badge color
  const getAgentBadgeColor = (agent: string) => {
    if (!agent) {
return 'bg-gray-100 text-gray-800';
}
    if (agent.includes('Encompass')) {
return 'bg-blue-100 text-blue-800';
}
    if (agent.includes('Rate')) {
return 'bg-green-100 text-green-800';
}
    if (agent.includes('Pre-Approval')) {
return 'bg-purple-100 text-purple-800';
}
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top Questions</CardTitle>
        <CardDescription>
          Most frequently asked questions across all agents
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40%]">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 font-medium hover:bg-transparent"
                  onClick={() => handleSort('question')}
                >
                  Question
                  <ArrowUpDown className="ml-2 h-3 w-3" />
                </Button>
              </TableHead>
              <TableHead className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 font-medium hover:bg-transparent"
                  onClick={() => handleSort('count')}
                >
                  Count
                  <ArrowUpDown className="ml-2 h-3 w-3" />
                </Button>
              </TableHead>
              <TableHead className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 font-medium hover:bg-transparent"
                  onClick={() => handleSort('avgRating')}
                >
                  Avg Rating
                  <ArrowUpDown className="ml-2 h-3 w-3" />
                </Button>
              </TableHead>
              <TableHead className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 font-medium hover:bg-transparent"
                  onClick={() => handleSort('avgResponseTime')}
                >
                  Avg Time
                  <ArrowUpDown className="ml-2 h-3 w-3" />
                </Button>
              </TableHead>
              <TableHead className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 font-medium hover:bg-transparent"
                  onClick={() => handleSort('avgCost')}
                >
                  Avg Cost
                  <ArrowUpDown className="ml-2 h-3 w-3" />
                </Button>
              </TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedQuestions.map((question) => (
              <React.Fragment key={question.id}>
                <TableRow
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setExpandedRow(expandedRow === question.id ? null : question.id)}
                >
                  <TableCell>
                    <div className="space-y-1">
                      <p className="font-medium text-sm line-clamp-2">
                        {question.question}
                      </p>
                      <Badge variant="outline" className={getAgentBadgeColor(question.primaryAgent)}>
                        {question.primaryAgent}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <MessageSquare className="h-3 w-3 text-muted-foreground" />
                      <span className="font-medium">{question.count}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <Star className="h-3 w-3 text-yellow-500" />
                      <span>{question.avgRating.toFixed(1)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">
                        {(question.avgResponseTime / 1000).toFixed(1)}s
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <DollarSign className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">
                        {question.avgCost.toFixed(4)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {expandedRow === question.id ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </TableCell>
                </TableRow>

                {/* Expanded row with additional details */}
                {expandedRow === question.id && (
                  <TableRow>
                    <TableCell colSpan={6} className="bg-muted/30">
                      <div className="p-4 space-y-3">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">
                              Sample Keywords
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {question.sampleKeywords?.map((keyword, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {keyword}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">
                              Documents Used
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {question.documentsUsed?.map((doc, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {doc}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">
                              Performance Metrics
                            </p>
                            <div className="space-y-1 text-sm">
                              <p>Total cost: ${(question.count * question.avgCost).toFixed(2)}</p>
                              <p>Success rate: {((question.avgRating / 5) * 100).toFixed(0)}%</p>
                              <p>Total time: {(question.count * question.avgResponseTime / 1000 / 60).toFixed(1)} min</p>
                            </div>
                          </div>
                        </div>
                        {question.sampleAnswer && (
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">
                              Sample Answer Preview
                            </p>
                            <p className="text-sm text-muted-foreground bg-background p-3 rounded-md">
                              {question.sampleAnswer}
                            </p>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>

        {/* Summary footer */}
        <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-muted-foreground">
          <div>
            Showing top {questions.length} questions
          </div>
          <div className="flex items-center space-x-4">
            <span>Total queries: {questions.reduce((sum, q) => sum + q.count, 0)}</span>
            <span>Avg rating: {(questions.reduce((sum, q) => sum + q.avgRating, 0) / questions.length).toFixed(1)}</span>
            <span>Total cost: ${questions.reduce((sum, q) => sum + q.count * q.avgCost, 0).toFixed(2)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}